// src/hooks/useProjectsData.js

import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { listProjects, createProject, removeProject, renameProject } from '../services/projectServices';
import { listKnowledgeBases } from '../services/kbServices';
import { listDocs, deleteDoc, renameDoc, runDocs } from '../services/docServices';

// Import localForage utils
import { getJsonLocalForage, setJsonLocalForage } from '../utils/localForageUtils';

const PROJECTS_CACHE_KEY = 'projectsCache';

// Utility function to create default phases for a project
function createDefaultPhases(projectId) {
  return [
    { id: `${projectId}_phase1`, name: 'Concept Exploration', kb_id: null, files: [], expanded: true },
    { id: `${projectId}_phase2`, name: 'Requirements', kb_id: null, files: [], expanded: true },
    { id: `${projectId}_phase3`, name: 'Architecture', kb_id: null, files: [], expanded: true },
    {
      id: `${projectId}_phase4`,
      name: 'Detailed Design',
      kb_id: null,
      files: [],
      expanded: true,
    },
    {
      id: `${projectId}_phase5`,
      name: 'Integration & Testing',
      kb_id: null,
      files: [],
      expanded: true,
    },
    {
      id: `${projectId}_phase6`,
      name: 'Manufacturing',
      kb_id: null,
      files: [],
      expanded: true,
    },
    {
      id: `${projectId}_phase7`,
      name: 'Operations & Maintenance',
      kb_id: null,
      files: [],
      expanded: true,
    },
  ];
}

export function useProjectsData() {
  // Initialize state with potential cached data
  const [projects, setProjects] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Flag for initial load
  
  // Get project_id from URL at the top level
  const { project_id } = useParams();
  
  // Simple in-memory object for doc metadata
  const docCacheRef = useRef({});

  // Load projects on mount
  useEffect(() => {
    async function initialLoad() {
      setLoading(true);
      setError(null);
      
      // Load from cache immediately - no waiting
      const cachedProjects = await getJsonLocalForage(PROJECTS_CACHE_KEY);
      if (cachedProjects && Array.isArray(cachedProjects)) {
        console.log("[ProjectData] Loaded projects from cache.");
        setProjects(cachedProjects); 
        setLoading(false); // Render cached data immediately
        setIsInitialLoad(false); // Mark initial load done
        
        // Background refresh - wait for initialization before API call
        waitForInitThenRefresh();
      } else {
        // No cache - wait for init then fetch
        await waitForInitThenRefresh();
        setIsInitialLoad(false);
      }
    }
    
    async function waitForInitThenRefresh() {
      // Only wait for API calls, not for rendering cached data
      // Use a more efficient check with requestAnimationFrame
      if (!window.__APP_INITIALIZED__) {
        await new Promise((resolve) => {
          let attempts = 0;
          const maxAttempts = 50; // 50 * 100ms = 5 seconds max
          
          const check = () => {
            if (window.__APP_INITIALIZED__ || attempts >= maxAttempts) {
              resolve();
            } else {
              attempts++;
              requestAnimationFrame(() => setTimeout(check, 100));
            }
          };
          
          check();
        });
      }
      
      // Now safe to make API calls
      await refreshProjects(false);
    }
    
    initialLoad();
  }, []); // Run only once on mount

  async function refreshProjects(showLoading = true) {
    if (showLoading) {
      setLoading(true);
    }
    // Keep existing error state unless we succeed
    try {
      const serverProjects = await listProjects();
      
      // Add default phases to each project since backend doesn't include phases
      const projectsWithPhases = serverProjects.map(project => ({
        ...project,
        expanded: true,
        phases: createDefaultPhases(project.id)
      }));
      
      // Load KBs and docs for the projects, passing project_id
      const loadedProjects = await loadKBsAndDocs(projectsWithPhases, project_id);
      
      // Update state with fully processed projects
      setProjects(loadedProjects);
      setError(null); // Clear error on success
      
      // Save the fresh, seeded projects to cache
      await setJsonLocalForage(PROJECTS_CACHE_KEY, loadedProjects);
      console.log("[ProjectData] Refreshed projects from server and updated cache.");
      
    } catch (err) {
      console.error('Failed to list/refresh projects:', err);
      // Only set error if it's not the initial load (where cache might still be shown)
      if (!isInitialLoad) {
           setError(err);
      }
    } finally {
      // Always set loading to false after refresh attempt, even if using cache
      setLoading(false);
    }
  }
  
  // Function to load KBs and docs for projects - optimized with parallel API calls
  async function loadKBsAndDocs(projectsToProcess, currentProjectId) {
    const processedProjects = [...projectsToProcess]; // Create a mutable copy
    try {
      // Get all KBs for this project - this is still a single call
      const existingKbs = await listKnowledgeBases(currentProjectId);
      console.debug(`[ProjectData] Found ${existingKbs.length} KBs for project ${currentProjectId}`);

      // Collect all phase KB matching and document loading promises for parallel execution
      const documentLoadingPromises = [];
      const phaseKbMappings = [];

      for (const proj of processedProjects) {
        // Skip projects that don't match the current project_id
        if (currentProjectId && proj.id !== currentProjectId) {
          console.log(`[ProjectData] Skipping project ${proj.id} as it doesn't match current ${currentProjectId}`);
          continue;
        }

        for (const phase of proj.phases) {
          // Robustly match KB by project_id and KB name ("KB for {phase.name}")
          const possibleName = `KB for ${phase.name}`;
          const found = existingKbs.find(
            kb => kb.project_id === proj.id && kb.name === possibleName
          );
          
          if (found) {
            phase.kb_id = found.id;
            
            // Store the mapping for later processing
            phaseKbMappings.push({ phase, kb_id: found.id });
            
            // Add document loading promise to parallel execution queue
            documentLoadingPromises.push(
              listDocs(phase.kb_id)
                .then(docs => {
                  return {
                  phase,
                  kb_id: found.id,
                  docs: docs.map((d) => ({
                    id: d.id,
                    name: d.name,
                    type: d.type,
                    run: d.run,
                    progress: d.progress,
                      location: d.location,
                    kb_id: phase.kb_id 
                  })),
                  success: true
                  };
                })
                .catch(err => {
                  console.error('Failed to fetch docs for', phase.name, err);
                  return {
                    phase,
                    kb_id: found.id,
                    docs: [],
                    success: false,
                    error: err
                  };
                })
            );
          } else {
            phase.kb_id = null; // Explicitly set to null if not found
            phase.files = []; // Ensure files is an empty array if no KB found
          }
        }
      }

      // Execute all document loading promises in parallel
      console.debug(`[ProjectData] Loading documents for ${documentLoadingPromises.length} knowledge bases in parallel`);
      const documentResults = await Promise.allSettled(documentLoadingPromises);

      // Process the results and update phases
      documentResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const { phase, kb_id, docs, success } = result.value;
          if (success) {
            phase.files = docs;
            // Update doc cache
            docCacheRef.current[kb_id] = docs;
            console.debug(`[ProjectData] Successfully loaded ${docs.length} documents for phase ${phase.name}`);
          } else {
            phase.files = [];
            console.warn(`[ProjectData] Failed to load documents for phase ${phase.name}`);
          }
        } else {
          console.error(`[ProjectData] Document loading promise ${index} was rejected:`, result.reason);
          // Find the corresponding phase and set empty files
          if (phaseKbMappings[index]) {
            phaseKbMappings[index].phase.files = [];
          }
        }
      });

      console.debug(`[ProjectData] Parallel document loading completed for ${processedProjects.length} projects`);
    } catch (topErr) {
      console.error('loadKBsAndDocs() error:', topErr);
      // Ensure projects remain in a consistent state (e.g., empty files arrays)
      processedProjects.forEach(proj => proj.phases.forEach(ph => ph.files = ph.files || []));
    }
    return processedProjects;
  }

  // Helper function that forces a refresh of files for a specific phase
  async function refreshPhaseFiles(projectId, phaseId) {
    console.log(`[ProjectData] Refreshing files for project ${projectId}, phase ${phaseId}`);

    const project = projects.find(p => p.id === projectId);
    if (!project) {
      console.error(`[ProjectData] Project ${projectId} not found for phase file refresh`);
      return;
    }

    const phase = project.phases.find(p => p.id === phaseId);
    if (!phase) {
      console.error(`[ProjectData] Phase ${phaseId} not found in project ${projectId}`);
      return;
    }

    if (!phase.kb_id) {
      console.log(`[ProjectData] Phase ${phaseId} has no KB ID, skipping refresh`);
      return;
    }

    // Show local docs first
    try {
      console.log(`[ProjectData] Fetching documents for KB ${phase.kb_id}`);
      const docs = await listDocs(phase.kb_id);
      const updatedFiles = docs.map((d) => ({
        id: d.id,
        name: d.name,
        type: d.type,
        run: d.run,
        progress: d.progress,
        location: d.location,
        kb_id: phase.kb_id 
      }));
      
      console.log(`[ProjectData] Found ${updatedFiles.length} documents, updating state`);
      
      // Update state
      setProjects((prev) => {
        return prev.map(proj => {
          if (proj.id === projectId) {
            return {
              ...proj,
              phases: proj.phases.map(ph => {
                if (ph.id === phaseId) {
                  return { ...ph, files: updatedFiles };
                }
                return ph;
              })
            };
          }
          return proj;
        });
      });
      
      console.log(`[ProjectData] Successfully refreshed phase ${phaseId} files`);
      
    } catch (err) {
      console.error('Failed to refresh phase files:', err);
      // On error, still update the state to clear loading indicators
      setProjects((prev) => {
        return prev.map(proj => {
          if (proj.id === projectId) {
            return {
              ...proj,
              phases: proj.phases.map(ph => {
                if (ph.id === phaseId) {
                  return { ...ph, files: [] };
                }
                return ph;
              })
            };
          }
          return proj;
        });
      });
    }
  }

  // Create a new project
  async function onCreateProject(name, description = '') {
    setLoading(true);
    try {
      const result = await createProject(name, description);
      const projectId = result.project_id;
      
      // Create a new project with default phases
      const newProject = {
        id: projectId,
        name: name,
        description: description,
        expanded: true,
        phases: createDefaultPhases(projectId)
      };
      
      // If knowledgebases were returned, map them to the corresponding phases
      if (result.knowledgebases && result.knowledgebases.length > 0) {
        newProject.phases.forEach(phase => {
          const kbMatch = result.knowledgebases.find(kb => kb.phaseName === phase.name);
          if (kbMatch) {
            phase.kb_id = kbMatch.id;
          }
        });
      }
      
      // Always add the new project to the current state immediately
      setProjects(prevProjects => {
        const updatedProjects = [...prevProjects, newProject];
        // Update cache
        setJsonLocalForage(PROJECTS_CACHE_KEY, updatedProjects);
        return updatedProjects;
      });
      
      // Still perform a full refresh to ensure we have complete data
      // but don't block the UI on this
      setTimeout(() => {
        refreshProjects(false).catch(err => {
          console.error("Background refresh after project creation failed:", err);
        });
      }, 300);
      
      return projectId; // Return the new ID
    } catch (err) {
        setError(err);
        throw err; // Re-throw for modal error handling
    } finally {
        setLoading(false);
    }
  }

  // Rename a project
  async function onRenameProject(projectId, newName) {
    setLoading(true);
    try {
      await renameProject(projectId, newName);
      await refreshProjects(); // Refresh list to show the change
    } catch (err) {
        setError(err);
        throw err; // Re-throw for modal error handling
    } finally {
        setLoading(false);
    }
  }

  // Delete a project
  async function onDeleteProject(projectId) {
    setLoading(true);
    try {
      await removeProject(projectId);
      await refreshProjects(); // Refresh list after deleting
    } catch (err) {
        setError(err);
        throw err; // Re-throw for modal error handling
    } finally {
        setLoading(false);
    }
  }

  // Update document state within the projects structure (used by polling)
  function updateDocInProjects(docId, updatedDoc) {
    setProjects(prevProjects => {
      const newProjects = [...prevProjects];
      
      for (const proj of newProjects) {
        for (const phase of proj.phases) {
          const fileIndex = phase.files.findIndex(f => f.id === docId);
          if (fileIndex >= 0) {
            // Update the file
            phase.files[fileIndex] = {
              ...phase.files[fileIndex],
              ...updatedDoc
            };
            
            // Update cache if needed
            if (phase.kb_id) {
              if (!docCacheRef.current[phase.kb_id]) {
                docCacheRef.current[phase.kb_id] = [];
              }
              
              const cacheIndex = docCacheRef.current[phase.kb_id].findIndex(f => f.id === docId);
              if (cacheIndex >= 0) {
                docCacheRef.current[phase.kb_id][cacheIndex] = {
                  ...docCacheRef.current[phase.kb_id][cacheIndex],
                  ...updatedDoc
                };
              }
            }
          }
        }
      }
      
      return newProjects;
    });
  }
  
  function getKbIdForFile(fileId) {
    for (const project of projects) {
      for (const phase of project.phases) {
        const file = phase.files.find(f => f.id === fileId);
        if (file && phase.kb_id) {
          return phase.kb_id;
        }
      }
    }
    return null;
  }
  
  // Get access to the document cache reference for external updates
  function getDocCacheRef() {
    return docCacheRef;
  }

  // Delete a document and update state
  async function deleteDocumentInProject(docId, kbId = null) {
    let targetKbId = kbId;
    if (!targetKbId) {
      targetKbId = getKbIdForFile(docId);
    }
    
    if (!targetKbId) {
      throw new Error(`Cannot delete doc ${docId}: KB ID not found.`);
    }

    try {
      await deleteDoc(docId);
      
      // Remove from local state immediately
      setProjects(prevProjects => {
        const newProjects = prevProjects.map(proj => ({
          ...proj,
          phases: proj.phases.map(phase => {
            if (phase.kb_id === targetKbId) {
              return {
                ...phase,
                files: phase.files.filter(file => file.id !== docId)
              };
            }
            return phase;
          })
        }));
        // Update cache after modifying state
        setJsonLocalForage(PROJECTS_CACHE_KEY, newProjects);
        return newProjects;
      });
      
      return true;
    } catch (err) {
      console.error(`Failed to delete document ${docId}:`, err);
      setError(err);
      throw err; // Re-throw
    }
  }

  // Rename a document and update state
  async function renameDocumentInProject(docId, newName, kbId = null) {
    let targetKbId = kbId;
    if (!targetKbId) {
      targetKbId = getKbIdForFile(docId);
    }
    
    if (!targetKbId) {
      throw new Error(`Cannot rename doc ${docId}: KB ID not found.`);
    }
    
    try {
      await renameDoc(targetKbId, docId, newName);
      console.log(`[ProjectData] Document ${docId} renamed to ${newName} in KB ${targetKbId}.`);
      
      // Update local state immediately
      setProjects(prevProjects => {
        const newProjects = prevProjects.map(proj => ({
          ...proj,
          phases: proj.phases.map(phase => {
            if (phase.kb_id === targetKbId) {
              const fileIndex = phase.files.findIndex(file => file.id === docId);
              if (fileIndex !== -1) {
                 const updatedFiles = [...phase.files];
                 updatedFiles[fileIndex] = { ...updatedFiles[fileIndex], name: newName };
                 return { ...phase, files: updatedFiles };
              } 
            }
            return phase;
          })
        }));
        // Update cache after modifying state
        setJsonLocalForage(PROJECTS_CACHE_KEY, newProjects);
        return newProjects;
      });
      
      return true;
    } catch (err) {
      console.error(`Failed to rename document ${docId}:`, err);
      setError(err);
      throw err; // Re-throw
    }
  }

  // Run document processing
  async function runDocumentsInProject(docIds, { deleteExisting = false } = {}) {
    try {
      // Ensure docIds is an array
      const docIdsArray = Array.isArray(docIds) ? docIds : [docIds];
      
      // 1. Call API to start document processing
      await runDocs(docIdsArray, deleteExisting ? { delete: true } : {});
      
      // 2. Immediately update local state to show processing
      for (const docId of docIdsArray) {
        updateDocInProjects(docId, {
          run: '1',
          progress: 0,
          progress_msg: 'Processing queued...'
        });
      }
      
      console.log(`[runDocumentsInProject] Processing queued for ${docIdsArray.length} document(s)`);
      return true;
    } catch (error) {
      console.error(`Error queuing document processing:`, error);
      return false;
    }
  }

  return {
    projects,
    setProjects,
    loading,
    error,
    refreshProjects,
    loadKBsAndDocs,
    refreshPhaseFiles,
    onCreateProject,
    onRenameProject,
    onDeleteProject,
    updateDocInProjects,
    getKbIdForFile,
    getDocCacheRef,
    // Centralized file operation functions
    deleteDocumentInProject,
    renameDocumentInProject,
    runDocumentsInProject
  };
} 